import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Award, Mail } from "lucide-react";
import SEO from "../components/SEO";
import "./BlogArticle.css";

export const StartupCostsTaxUK = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Business Startup Costs You Can Claim (UK Tax Guide)",
    "description": "Discover which startup costs are tax-deductible for UK businesses. Pre-trading expenses, capital allowances, and HMRC rules explained.",
    "author": {
      "@type": "Organization",
      "name": "Brightson Accounting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Brightson Accounting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://brightsonaccounting.co.uk/logo.png"
      }
    },
    "datePublished": "2026-04-10",
    "dateModified": "2026-04-10",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://brightsonaccounting.co.uk/blog/startup-costs-tax-uk"
    }
  };

  return (
    <div className="article-page">
      <SEO
        title="Business Startup Costs You Can Claim (UK Tax Guide)"
        description="Discover which startup costs are tax-deductible for UK businesses. Pre-trading expenses, capital allowances, and HMRC rules explained."
        canonical="/blog/startup-costs-tax-uk"
        keywords="startup costs tax deductible UK, business expenses UK, pre-trading expenses, capital allowances UK, HMRC allowable expenses, accountant Wolverhampton"
        schema={JSON.stringify(articleSchema)}
      />

      <section className="article-header">
        <div className="container">
          <div className="article-back">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
          </div>
          <div className="article-header-content">
            <span className="article-category-badge">Starting a Business</span>
            <h1 className="article-main-title">Business Startup Costs You Can Claim (UK Tax Guide)</h1>
            <p className="article-subtitle">Maximize Your Tax Relief from Brightson Accounting, Wolverhampton</p>
            <div className="article-meta-bar">
              <div className="meta-author">
                <Award size={18} />
                <span>Brightson Accounting (FCCA Qualified)</span>
              </div>
              <div className="meta-info">
                <Calendar size={16} />
                <span>10 April 2026</span>
                <Clock size={16} />
                <span>8 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="article-content">
        <div className="container">
          <div className="article-body">
            
            {/* Traffic boost hook */}
            <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', padding: '1rem', marginBottom: '2rem' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', color: '#92400e' }}>
                Most business owners overpay tax — <Link to="/corporation-tax-calculator" style={{ color: '#92400e', fontWeight: 600, textDecoration: 'underline' }}>see how much you could save using our calculator 👉</Link>
              </p>
            </div>

            <p className="article-lead">
              Most new business owners don't realize they can claim tax relief on many startup costs — including expenses paid before you officially started trading.
            </p>

            <p>
              From equipment and software to professional fees and marketing, HMRC allows you to deduct a wide range of pre-trading and startup expenses.
            </p>

            <p>
              At <Link to="/services">Brightson Accounting in Wolverhampton</Link>, we help new business owners across the West Midlands maximize their tax relief from day one.
            </p>

            <p>
              This guide explains exactly what you can and can't claim — and how to do it correctly.
            </p>

            <div className="info-box">
              <strong>Quick Summary</strong>
              <ul>
                <li>You can claim pre-trading expenses incurred up to 7 years before you start trading</li>
                <li>Allowable costs include equipment, software, marketing, professional fees, and training</li>
                <li>Keep receipts and records for everything</li>
                <li>Capital allowances apply to equipment and vehicles</li>
                <li>Personal expenses and entertainment are NOT allowable</li>
              </ul>
            </div>

            <h2>What Are Pre-Trading Expenses?</h2>

            <p>
              Pre-trading expenses are costs you incur <strong>before</strong> you officially start trading.
            </p>

            <p>
              Examples include:
            </p>

            <ul>
              <li>Market research and business planning</li>
              <li>Website development</li>
              <li>Advertising and marketing</li>
              <li>Professional fees (accountant, solicitor)</li>
              <li>Training and courses</li>
              <li>Equipment and software</li>
            </ul>

            <p>
              HMRC allows you to claim these costs as tax-deductible business expenses — as long as they would have been allowable if incurred after you started trading.
            </p>

            <h2>How Far Back Can You Claim?</h2>

            <p>
              You can claim pre-trading expenses incurred up to <strong>7 years</strong> before you start trading.
            </p>

            <p>
              Example:
            </p>

            <ul>
              <li>You attend a business course in 2022 (£500)</li>
              <li>You start trading in 2026</li>
              <li>You can still claim the £500 as a pre-trading expense in your first tax return</li>
            </ul>

            <p className="info-box">
              💡 <strong>Pro Tip:</strong><br/>
              From what we see with clients in Wolverhampton, most people can claim £2,000–£5,000 in pre-trading expenses — but only if they've kept receipts.
            </p>

            {/* Mid-Article CTA */}
            <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
              <h3>💡 Want to see how much tax you could save?</h3>
              <p>
                Most UK business owners are overpaying without realising it.
              </p>
              <Link to="/corporation-tax-calculator" className="article-cta-button">
                👉 Try the Corporation Tax Calculator
              </Link>
            </div>

            <h2>Allowable Startup Costs</h2>

            <h3>1. Equipment and Tools</h3>

            <p>
              Laptops, desks, tools, machinery — anything you need to run your business.
            </p>

            <p>
              Claim via <strong>capital allowances</strong> if the item costs over £1,000 (or deduct immediately if under).
            </p>

            <h3>2. Software and Subscriptions</h3>

            <p>
              Accounting software, design tools, domain names, website hosting, email subscriptions.
            </p>

            <h3>3. Marketing and Advertising</h3>

            <p>
              Website development, business cards, flyers, social media ads, SEO, photography.
            </p>

            <h3>4. Professional Fees</h3>

            <p>
              Accountant, solicitor, business coach, trademark registration.
            </p>

            <h3>5. Training and Courses</h3>

            <p>
              Business courses, industry qualifications, software training.
            </p>

            <h3>6. Stock and Materials</h3>

            <p>
              Raw materials, stock for resale, packaging.
            </p>

            <h3>7. Premises Costs</h3>

            <p>
              Rent, utilities, business rates (if you have a separate business premises).
            </p>

            <p>
              If you work from home, you can claim a portion of rent/mortgage interest, utilities, and council tax.
            </p>

            <h3>8. Insurance</h3>

            <p>
              Professional indemnity, public liability, equipment insurance.
            </p>

            <h3>9. Travel and Mileage</h3>

            <p>
              Business-related travel (client meetings, site visits, supplier visits).
            </p>

            <p>
              Claim either:
            </p>

            <ul>
              <li>Actual costs (fuel, parking, tolls)</li>
              <li>HMRC mileage rate (45p per mile for first 10,000 miles, then 25p)</li>
            </ul>

            <p className="highlight-note">
              Commuting between home and your regular place of work is NOT allowable.
            </p>

            <h3>10. Bank Charges and Finance Costs</h3>

            <p>
              Business account fees, loan interest, credit card interest (for business purchases).
            </p>

            <h2>What You CANNOT Claim</h2>

            <p>
              HMRC has strict rules on what's not allowable:
            </p>

            <ul>
              <li><strong>Personal expenses</strong> — clothes (unless branded workwear), gym memberships, personal meals</li>
              <li><strong>Entertainment</strong> — client dinners, hospitality, gifts over £50</li>
              <li><strong>Fines and penalties</strong> — parking fines, late tax penalties</li>
              <li><strong>Personal use of assets</strong> — if you use a laptop 50% personally, you can only claim 50% of the cost</li>
            </ul>

            <h2>Capital Allowances Explained</h2>

            <p>
              For expensive items (equipment, vehicles, machinery), you claim <strong>capital allowances</strong> instead of deducting the full cost immediately.
            </p>

            <h3>Annual Investment Allowance (AIA)</h3>

            <p>
              You can deduct <strong>100% of the cost</strong> in the first year for most equipment — up to £1 million.
            </p>

            <p>
              This means:
            </p>

            <ul>
              <li>Buy a £5,000 laptop → claim £5,000 in year 1</li>
              <li>Buy a £20,000 van → claim £20,000 in year 1</li>
            </ul>

            <h3>Writing Down Allowance (WDA)</h3>

            <p>
              For items not covered by AIA (or if you exceed the £1m limit), you can claim:
            </p>

            <ul>
              <li>18% per year for most equipment</li>
              <li>6% per year for integral building features (e.g., heating systems)</li>
            </ul>

            <h2>How to Claim Startup Costs</h2>

            <h3>For Sole Traders:</h3>

            <p>
              Include all allowable expenses in your first Self Assessment tax return.
            </p>

            <p>
              Under "Business expenses", list:
            </p>

            <ul>
              <li>Equipment (capital allowances)</li>
              <li>Marketing and advertising</li>
              <li>Professional fees</li>
              <li>Training</li>
              <li>Other pre-trading expenses</li>
            </ul>

            <h3>For Limited Companies:</h3>

            <p>
              Include startup costs in your first set of accounts and Corporation Tax return (CT600).
            </p>

            <p>
              Your <Link to="/services">accountant in Wolverhampton</Link> will handle this for you.
            </p>

            <h2>Record-Keeping Rules</h2>

            <p>
              HMRC requires you to keep records for:
            </p>

            <ul>
              <li><strong>Sole traders:</strong> 5 years after the 31 January filing deadline</li>
              <li><strong>Limited companies:</strong> 6 years from the end of the accounting period</li>
            </ul>

            <p>
              Keep:
            </p>

            <ul>
              <li>Receipts (paper or digital)</li>
              <li>Invoices</li>
              <li>Bank statements</li>
              <li>Mileage logs</li>
              <li>Contracts and agreements</li>
            </ul>

            <p>
              Use accounting software (Xero, QuickBooks, FreeAgent) to store and categorize everything automatically.
            </p>

            <p className="highlight-note">
              Not sure what software to use? <Link to="/contact">Speak to an accountant</Link>.
            </p>

            <h2>Common Mistakes to Avoid</h2>

            <p>
              We see the same errors with new business owners in Birmingham and Wolverhampton:
            </p>

            <ul>
              <li>Not keeping receipts for pre-trading expenses</li>
              <li>Claiming personal expenses as business costs</li>
              <li>Mixing personal and business finances</li>
              <li>Over-claiming on home office costs</li>
              <li>Not claiming capital allowances correctly</li>
            </ul>

            <p>
              Avoid them all: <Link to="/blog/business-mistakes-uk">Common Mistakes New Business Owners Make</Link>
            </p>

            <h2>How Much Can You Save?</h2>

            <p>
              Tax relief depends on your business structure:
            </p>

            <h3>Sole Trader Example:</h3>

            <ul>
              <li>Startup costs: £5,000</li>
              <li>Tax band: 20%</li>
              <li>Tax saving: £1,000</li>
            </ul>

            <h3>Limited Company Example:</h3>

            <ul>
              <li>Startup costs: £10,000</li>
              <li>Corporation Tax: 19%</li>
              <li>Tax saving: £1,900</li>
            </ul>

            <p>
              Every receipt counts — and proper record-keeping can save you hundreds or thousands in tax.
            </p>

            <div className="article-cta-box">
              <h2>Need help starting your business?</h2>
              <p>
                We help business owners across Wolverhampton and the West Midlands set up correctly, avoid tax mistakes, and stay compliant.
              </p>
              <Link to="/contact" className="article-cta-button">
              <Mail size={20} />
                Book a Free Consultation
              </Link>
            </div>

            <div className="disclaimer">
              <h3>Disclaimer</h3>
              <p>
                This content is for general guidance only and based on UK tax rules as of April 2026. Tax rules may change. 
                For tailored advice, contact Brightson Accounting.
              </p>
            </div>

          </div>

          <aside className="article-sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-heading">Need Help?</h3>
              <p>Speak to our FCCA-qualified accountants about claiming startup costs.</p>
              <Link to="/contact" className="sidebar-button">
                <Mail size={18} />
                Contact Us
              </Link>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Related Articles</h3>
              <div className="related-links">
                <Link to="/blog/start-business-uk-2026" className="related-link">How to Start a Business UK</Link>
                <Link to="/blog/business-tax-uk-guide" className="related-link">Business Tax Guide</Link>
                <Link to="/blog/business-mistakes-uk" className="related-link">Common Business Mistakes</Link>
                <Link to="/start-a-business-uk" className="related-link">Complete Business Startup Hub</Link>
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Quick Links</h3>
              <div className="related-links">
                <Link to="/services" className="related-link">Our Services</Link>
                <Link to="/pricing" className="related-link">Pricing</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default StartupCostsTaxUK;
